import { createContext, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Lock } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import logoPrincipal from '@/assets/logo-principal.png'

interface PortalSession {
  token: string
  session: string
}

const PortalSessionContext = createContext<PortalSession | null>(null)

export function usePortalSession(): PortalSession {
  const ctx = useContext(PortalSessionContext)
  if (!ctx) throw new Error('usePortalSession precisa estar dentro do PortalGate')
  return ctx
}

/** Sessão guardada por evento (token do link). */
const chave = (token: string) => `portal_session_${token}`

function lerSessao(token: string): string | null {
  try {
    const bruto = localStorage.getItem(chave(token))
    if (!bruto) return null
    const dados = JSON.parse(bruto) as { session_token?: string; expires_at?: string }
    if (!dados.session_token || !dados.expires_at) return null
    if (new Date(dados.expires_at).getTime() <= Date.now()) {
      localStorage.removeItem(chave(token))
      return null
    }
    return dados.session_token
  } catch {
    return null
  }
}

function gravarSessao(token: string, session_token: string, expires_at: string) {
  try {
    localStorage.setItem(chave(token), JSON.stringify({ session_token, expires_at }))
  } catch {
    /* navegador sem storage: a sessão vale só para esta aba */
  }
}

export function limparSessaoPortal(token: string) {
  try {
    localStorage.removeItem(chave(token))
  } catch {
    /* ignore */
  }
}

export function PortalGate({ children }: { children: React.ReactNode }) {
  const { token } = useParams()
  const [session, setSession] = useState<string | null>(null)
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [pronto, setPronto] = useState(false)

  useEffect(() => {
    if (!token) return
    let ativo = true
    const guardada = lerSessao(token)
    if (!guardada) {
      setSession(null)
      setPronto(true)
      return
    }
    // Confere no servidor: o código pode ter sido trocado ou os aparelhos desconectados.
    supabase
      .rpc('portal_sessao_evento', { p_token: token, p_session: guardada })
      .then(({ error }) => {
        if (!ativo) return
        // Só descarta a sessão quando o servidor respondeu que ela não vale mais.
        // Falha de conexão (sem código de erro do banco) mantém o acesso salvo.
        const recusadaPeloServidor = !!error && !!(error as { code?: string }).code
        if (recusadaPeloServidor) {
          limparSessaoPortal(token)
          setSession(null)
        } else {
          setSession(guardada)
        }
        setPronto(true)
      })
    return () => {
      ativo = false
    }
  }, [token])

  const validar = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setErro(null)
    setEnviando(true)
    const { data, error } = await supabase.rpc('validar_codigo_acesso_cliente', {
      p_token: token,
      p_codigo: codigo.trim(),
    })
    setEnviando(false)

    if (error) {
      setErro('Não foi possível validar o código agora. Tente de novo.')
      return
    }

    const res = (data ?? {}) as Record<string, any>
    if (res.ok) {
      gravarSessao(token, res.session_token, res.expires_at)
      setSession(res.session_token)
      return
    }

    switch (res.motivo) {
      case 'bloqueado': {
        const minutos = res.bloqueado_ate
          ? Math.max(1, Math.ceil((new Date(res.bloqueado_ate).getTime() - Date.now()) / 60000))
          : 15
        setErro(`Muitas tentativas. Tente de novo em ${minutos} minuto${minutos > 1 ? 's' : ''}.`)
        break
      }
      case 'codigo_incorreto': {
        const restantes = Number(res.tentativas_restantes ?? 0)
        setErro(
          `Código incorreto. ${restantes} tentativa${restantes === 1 ? '' : 's'} restante${
            restantes === 1 ? '' : 's'
          }.`,
        )
        break
      }
      case 'sem_codigo':
        setErro('Ainda não existe um código para este link. Peça o código para a sua assessoria.')
        break
      case 'link_invalido':
        setErro('Link inválido ou expirado. Peça um link novo para a sua assessoria.')
        break
      default:
        setErro('Código inválido. Digite os 6 números que você recebeu.')
    }
  }

  if (!token) return null

  if (!pronto) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (session) {
    return (
      <PortalSessionContext.Provider value={{ token, session }}>{children}</PortalSessionContext.Provider>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <img src={logoPrincipal} alt="Missão Cerimonial" className="mx-auto mb-3 h-10 object-contain" />
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-5 w-5 text-primary" />
          </div>
          <CardTitle className="text-xl">Código de acesso</CardTitle>
          <CardDescription>
            Digite os 6 números que a sua assessoria enviou. Depois disso, este aparelho não vai pedir de novo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={validar} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="codigo-acesso">Seu código</Label>
              <Input
                id="codigo-acesso"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                className="h-12 text-center text-2xl tracking-[0.4em]"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>
            {erro && <p className="text-sm text-destructive">{erro}</p>}
            <Button type="submit" className="min-h-[48px] w-full" disabled={enviando || codigo.length !== 6}>
              {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Entrar
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
